import { useState } from "react";
import { SeoHead } from "../components/SeoHead";
import { PremiumGate } from "../components/PremiumGate";
import { ChefHat, Save, ShoppingCart, ArrowUpRight, CalendarDays } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { track } from "../lib/analytics";
import { DAYS, CATALOG, CATALOG_GROUPS, getMeal, loadPlan, savePlan, generateFromPlan } from "../lib/planner";
import { getCollection } from "../data/collections";

const PRESETS = getCollection("meal-plan").recipes.filter((r) => r.week);

const DaySlot = ({ day, value, onChange }) => {
  const meal = getMeal(value);
  return (
    <div className="brut-card p-4 space-y-2" data-testid={`mealplan-${day}`}>
      <div className="flex items-center justify-between">
        <span className="font-display font-black uppercase text-sm">{day}</span>
        {meal ? (
          <Link to={meal.path} data-testid={`mealplan-open-${day}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-brand-primary hover:underline">
            Open recipe <ArrowUpRight size={12} />
          </Link>
        ) : <ChefHat size={14} className="text-foreground/40" />}
      </div>
      <select
        value={value}
        onChange={(e) => onChange(day, e.target.value)}
        data-testid={`mealplan-select-${day}`}
        className="w-full bg-ink border-2 border-white/30 p-2 font-mono text-sm focus:border-brand-primary outline-none"
      >
        <option value="">— Nothing planned —</option>
        {CATALOG_GROUPS.map((g) => (
          <optgroup key={g} label={g}>
            {CATALOG.filter((m) => m.group === g).map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
          </optgroup>
        ))}
      </select>
      {meal && (
        <div className="font-mono text-[10px] uppercase tracking-widest text-foreground/50" data-testid={`mealplan-ingredients-${day}`}>
          {meal.ingredients.length} ingredients · {meal.ingredients.map((i) => i.text).join(", ")}
        </div>
      )}
    </div>
  );
};

export default function MealPlan() {
  const [plan, setPlan] = useState(loadPlan);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();

  const apply = (next) => { setPlan(next); savePlan(next); };
  const update = (day, value) => apply({ ...plan, [day]: value });

  const onSave = () => {
    savePlan(plan);
    setSaved(true);
    track("meal_plan_saved", { day_count: DAYS.filter((d) => plan[d]).length });
    setTimeout(() => setSaved(false), 2200);
  };

  const onGenerate = () => {
    savePlan(plan);
    generateFromPlan(plan);
    track("grocery_generated_from_plan");
    navigate("/grocery-list");
  };

  return (
    <div data-testid="mealplan-page" className="space-y-6">
      <SeoHead
        title="Weekly Meal Plan — Cheese on Toast Premium"
        description="Plan a week of dinners. Auto-generates your grocery list."
        canonicalPath="/meal-plan"
      />

      <header className="space-y-2 pt-1">
        <span className="label-tag">PREMIUM · MEAL PLAN</span>
        <h1 className="font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl leading-[0.95]">
          This Week's Plan
        </h1>
        <p className="font-mono text-sm text-foreground/70">
          Pick a recipe for each night. One tap builds the shopping list.
        </p>
      </header>

      <PremiumGate
        title="Weekly Meal Plan"
        description="Plan a week of dinners and auto-generate your shopping list. Premium feature."
      >
        <div className="flex flex-wrap gap-2" data-testid="mealplan-presets">
          {PRESETS.map((p) => (
            <button key={p.slug} type="button" data-testid={`mealplan-preset-${p.slug}`} onClick={() => apply({ ...p.week })} className="btn-arcade btn-ghost px-3 py-2 text-xs">
              <CalendarDays size={14} /> Load {p.title}
            </button>
          ))}
        </div>

        <section data-testid="mealplan-grid" className="space-y-2">
          {DAYS.map((day) => <DaySlot key={day} day={day} value={plan[day] || ""} onChange={update} />)}
        </section>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button type="button" data-testid="mealplan-save" onClick={onSave} className="btn-arcade">
            <Save size={18} /> {saved ? "Saved ✓" : "Save Week"}
          </button>
          <button type="button" data-testid="mealplan-to-grocery" onClick={onGenerate} className="btn-arcade btn-ghost">
            <ShoppingCart size={18} /> Generate Grocery List
          </button>
        </div>
      </PremiumGate>
    </div>
  );
}
